import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import type { ChapterResultGroup, NoteExample } from '../../utils/notesSearch';

interface SearchResultsProps {
  /** null = no search has run yet; [] = searched with no matches; otherwise grouped matches. */
  groups: ChapterResultGroup[] | null;
}

const sourceLinkClasses =
  'inline-flex items-center gap-1 min-h-[24px] text-primary-700 underline hover:text-primary-900 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded';

/**
 * Where this example came from: a page link, plus a section link when the example sits under a
 * heading. Both open the reader in a new tab so the search and its results survive the trip
 * (FR-035/FR-036); the section link adds the heading's anchor so the reader scrolls to it.
 *
 * `from=search` tells the reader it was opened in a throwaway tab, so it offers Close (return to
 * this tab) instead of Back (navigate to the Learn tab).
 *
 * The reader's Close works by `window.close()`, NOT via `window.opener` — since Chrome 88 an
 * anchor's `target="_blank"` implies `rel="noopener"`, so `window.opener` is null here whether or
 * not we spell the rel out (measured: dropping it changes nothing). Restoring the opener would take
 * an explicit `rel="opener"`, which we don't need and wouldn't want.
 */
function SourceLinks({ example }: { example: NoteExample }) {
  const chapterHref = `/learn/${example.chapterSlug}`;
  const pageHref = `${chapterHref}?page=${example.pageNumber}&from=search`;
  // The heading may sit pages earlier than the example, so the section link targets the heading's
  // own page — otherwise the anchor is not on the page we open and the jump does nothing.
  const sectionHref = `${chapterHref}?page=${example.sectionPage}&from=search#${example.sectionAnchor}`;

  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-soft mb-2">
      <a
        href={pageHref}
        target="_blank"
        rel="noopener noreferrer"
        className={sourceLinkClasses}
        aria-label={`Open page ${example.pageNumber} of chapter ${example.chapterOrder} in a new tab`}
      >
        Page {example.pageNumber}
        <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
      </a>
      {example.sectionTitle && example.sectionAnchor && (
        <>
          <span aria-hidden="true" className="text-[#c9c1ab]">
            |
          </span>
          <a
            href={sectionHref}
            target="_blank"
            rel="noopener noreferrer"
            className={sourceLinkClasses}
            aria-label={`Open the section "${example.sectionTitle}" in a new tab`}
          >
            {example.sectionTitle}
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
          </a>
        </>
      )}
    </p>
  );
}

/**
 * Feature 022 — chapter-grouped search results (spec FR-027..FR-033). Each match renders the
 * example's authored context line, hand, auction, question and coaches'/editors' comments (the HTML
 * is the chapter's own markup, styled by `learn-chapter.css`); multiple matches from one chapter are
 * separated by a line partition.
 */
export function SearchResults({ groups }: SearchResultsProps) {
  if (groups === null) {
    return (
      <p className="text-ink-soft">Build a bidding sequence and press Search to find examples.</p>
    );
  }

  if (groups.length === 0) {
    return <p className="text-ink-soft">No matching examples.</p>;
  }

  return (
    <div className="space-y-8">
      {groups.map((group) => (
        <section key={group.slug} aria-label={`Chapter ${group.order} - ${group.title}`}>
          <h3 className="text-lg font-semibold text-ink mb-3 bg-cream border border-line-strong rounded-md px-3 py-2">
            Chapter {group.order} - {group.title}
          </h3>
          <div className="space-y-4">
            {group.examples.map((ex, i) => (
              <div key={i}>
                {i > 0 && <hr className="border-line-strong my-4" />}
                <div className="b-search-result">
                  <SourceLinks example={ex} />
                  {ex.contextLine && (
                    <p className="b-handintro font-medium text-ink mb-1">{ex.contextLine}</p>
                  )}
                  <div className="flex flex-wrap items-start gap-4">
                    <div
                      className="b-hand"
                      dangerouslySetInnerHTML={{ __html: ex.handHtml }}
                    />
                    <div className="min-w-0">
                      <div
                        className="overflow-x-auto"
                        dangerouslySetInnerHTML={{ __html: ex.auctionHtml }}
                      />
                      {ex.questionHtml && (
                        <p
                          className="b-cellquestion mt-2"
                          dangerouslySetInnerHTML={{ __html: ex.questionHtml }}
                        />
                      )}
                      {ex.commentsHtml.map((html, ci) => (
                        <p
                          key={ci}
                          className="mt-1 text-ink"
                          dangerouslySetInnerHTML={{ __html: html }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
